

import React, { useEffect, useMemo, useState } from 'react';
import { WinningResult } from './DemoBingoTickets';
import { drawDetailsProps } from '../components/apis/getDrawDetail';

interface WinnersModalProps {
  onClose: () => void;
  showWinners?: boolean;
  winners: {
    [ticketId: string]: WinningResult[];
  };
  drawDetails: drawDetailsProps | undefined;
  reducedCount: number;
}

interface RandomUser {
  name: string;
  totalPrice: number;
  patterns: {
    type: string;
    winCount: number;
    price: number;
  }[];
}

function generateMeaningfulName() {
  const prefixes = ['Al', 'Be', 'Cor', 'Da', 'El', 'Fa', 'Ga', 'Ha', 'Il', 'Ja', 'Ka'];
  const middles = ['an', 'el', 'or', 'ar', 'en', 'il', 'on', 'ir', 'ul'];
  const suffixes = ['dor', 'ian', 'eth', 'os', 'ius', 'ar', 'us', 'in'];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const middle = middles[Math.floor(Math.random() * middles.length)];
  const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
  const name = prefix + middle + suffix;
  return name.length > 12 ? name.slice(0, 12) : name;
}

const PlayerRow: React.FC<{ price: number; name: string; isWinner?: boolean }> = ({ price, name, isWinner }) => {
  return (
    <div
      style={{ backgroundImage: `url('/assets/winnerUserBg.png')` }}
      className="flex items-center w-full h- justify-between px-4 py-2 rounded-lg"
    >
      <span className={`mr-1 text-yellow-400`}>
        <img src="/assets/crone.png" alt="crone" width={25} height={25} />
      </span>
      <img src="/assets/user.png" alt="user" width={35} height={35} className="px-1" />
      <span className="flex-1 text-white text-sm">{isWinner ? 'You' : name}</span>
      <div className="text-sm flex justify-center items-center">
        <img src="/assets/coin.png" alt="coin" width={28} height={15} />
        <span className="text-white text-[12px] font-bold p-1">{price}</span>
      </div>
    </div>
  );
};

const DemoWinnersPopup: React.FC<WinnersModalProps> = ({
  onClose,
  drawDetails,
  winners,
  reducedCount,
  showWinners,
}) => {
  const patterns = drawDetails?.draw.prizes;

  const patternClaimCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(winners).forEach((patterns: any[]) => {
      patterns.forEach((pattern) => {
        if (pattern.isWinning) {
          const type = pattern.patternType;
          counts[type] = (counts[type] || 0) + 1;
        }
      });
    });
    return counts;
  }, [winners]);

  const filteredPatterns = useMemo(() => {
    if (!patterns) return {};
    const winningPatternTypes = Object.keys(patternClaimCounts);
    const filtered: Record<string, { price: number }> = {};
    for (const patternType in patterns) {
      if (winningPatternTypes.includes(patternType)) {
        filtered[patternType] = patterns[patternType];
      }
    }
    return filtered;
  }, [patterns, patternClaimCounts]);

  const patternEarnings = useMemo(() => {
    const earnings: Record<string, number> = {};
    if (!filteredPatterns) return earnings;
    for (const patternType in filteredPatterns) {
      const claimCount = patternClaimCounts[patternType] || 0;
      const price = filteredPatterns[patternType]?.price || 0;
      earnings[patternType] = claimCount * price;
    }
    return earnings;
  }, [filteredPatterns, patternClaimCounts]);

  const randomUsersCount = Math.floor(Math.random() * reducedCount + 1);

  const totalPrice = Object.entries(patternEarnings).reduce((acc, cur) => {
    return acc + cur[1];
  }, 0);

  const remainingPatterns = useMemo(() => {
    if (!patterns) return [];
    const result = [];
    for (const type in patterns) {
      const totalWinCount = patterns[type].winCount || 0;
      const claimedCount = patternClaimCounts[type] || 0;
      const remainingCount = totalWinCount - claimedCount;
      result.push({
        type: type,
        winCount: remainingCount,
        price: patterns[type].price,
      });
    }
    return result;
  }, [patterns, patternClaimCounts]);

  const [generatedUsers, setGeneratedUsers] = useState<RandomUser[]>([]);

  useEffect(() => {
    if (generatedUsers.length > 0) return;

    const totalAvailableWins = remainingPatterns.reduce((acc, pattern) => {
      return acc + (pattern.winCount || 0);
    }, 0);

    const adjustedUsersCount = Math.min(randomUsersCount, totalAvailableWins);

    if (adjustedUsersCount === 0 || totalAvailableWins === 0) {
      setGeneratedUsers([]);
      return;
    }

    const users: RandomUser[] = Array.from({ length: adjustedUsersCount }, () => ({
      name: generateMeaningfulName(),
      totalPrice: 0,
      patterns: [],
    }));

    const patternsToDistribute = remainingPatterns
      .filter((pattern) => pattern.winCount > 0)
      .map((pattern) => ({ ...pattern }));

    let patternIndex = 0;
    for (let i = 0; i < users.length; i++) {
      while (
        patternIndex < patternsToDistribute.length &&
        patternsToDistribute[patternIndex].winCount <= 0
      ) {
        patternIndex++;
      }

      if (patternIndex >= patternsToDistribute.length) break;

      const pattern = patternsToDistribute[patternIndex];
      const winsToAssign = 1;
      const priceForWins = winsToAssign * pattern.price;

      users[i].patterns.push({
        type: pattern.type,
        winCount: winsToAssign,
        price: priceForWins,
      });

      users[i].totalPrice += priceForWins;

      pattern.winCount -= winsToAssign;

      if (pattern.winCount === 0) {
        patternIndex++;
      }
    }
    patternsToDistribute.forEach((pattern) => {
      let remainingWinCount = pattern.winCount;

      while (remainingWinCount > 0) {
        const userIndex = Math.floor(Math.random() * users.length);

        const winsToAssign = Math.max(1, Math.floor(Math.random() * remainingWinCount));
        const priceForWins = winsToAssign * pattern.price;

        users[userIndex].patterns.push({
          type: pattern.type,
          winCount: winsToAssign,
          price: priceForWins,
        });

        users[userIndex].totalPrice += priceForWins;

        remainingWinCount -= winsToAssign;
      }
    });

    setGeneratedUsers(users.sort((a, b) => b.totalPrice - a.totalPrice));
  }, [remainingPatterns, randomUsersCount]);

  // Combine winners and generated users, then sort by price in descending order
  const combinedUsers = useMemo(() => {
    // Add the winner (with name "You") if there is a totalPrice > 0
    const winnerUser = 1 > 0 ? [{ name: 'You', totalPrice, patterns: [], isWinner: true }, { name: 'You', totalPrice, patterns: [], isWinner: true }, { name: 'You', totalPrice, patterns: [], isWinner: true }, { name: 'You', totalPrice, patterns: [], isWinner: true }, { name: 'You', totalPrice, patterns: [], isWinner: true }, { name: 'You', totalPrice, patterns: [], isWinner: true }, { name: 'You', totalPrice, patterns: [], isWinner: true }] : [];

    // Add the generated users
    const allUsers = [
      ...winnerUser,
      ...generatedUsers.map((user) => ({
        ...user,
        isWinner: false,
      })),
    ];

    // Sort by totalPrice in descending order
    return allUsers.sort((a, b) => b.totalPrice - a.totalPrice);
  }, [totalPrice, generatedUsers]);


  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      <div className="relative bg-transparent lg:max-w-[450px] 2xl:max-w-[500px] max-h-[800px] max-w-[450px] h-auto  flex flex-col items-center justify-center">
        <img
          src={'/assets/winnersBg.png'}
          alt="Popup Background"
          className="w-full h-full object-contain"
        />

        <img
          src={'/assets/winners.png'}
          alt="Winners"
          className="absolute top-[25px] md:w-[350px] md:h-[110px] w-[250px]"
          style={{ transform: 'translateY(-50%)' }}
        />

        <div
          className="absolute top-[30px] right-[30px] w-8 h-8 flex items-center justify-center bg-blue-700 rounded-full cursor-pointer"
          onClick={onClose}
        >
          <span className="text-white text-xl">×</span>
        </div>

        <div className="absolute top-[62px] w-[90%] px-4 h-[250px] xs:h-[290px] sm:h-[330px] 2xl:h-[380px] py-4 overflow-y-auto custom-scrollbar max-w-[450px] space-y-3">
          {combinedUsers.map((user, index) => (
            <PlayerRow
              key={index}
              price={user.totalPrice}
              name={user.name}
              isWinner={user.isWinner}
            />
          ))}
        </div>

        <button
          onClick={onClose}
          className="absolute text-white bottom-2 w-[145px] h-[50px]"
          style={{
            backgroundImage: `url(${'/assets/blueButton.png'})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

export default DemoWinnersPopup;